import React, { useState } from 'react';
import { StepTracker } from './StepTracker';
import { allFeatures, executeSteps, getTotalSteps } from '../utils/featureSteps';
import type { Step } from './StepTracker';

export const GoalTracker: React.FC = () => {
  const [selectedFeature, setSelectedFeature] = useState<keyof typeof allFeatures | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionSpeed, setExecutionSpeed] = useState(500);

  const handleFeatureSelect = (featureKey: keyof typeof allFeatures) => {
    const feature = allFeatures[featureKey];
    setSelectedFeature(featureKey);
    setSteps([...feature.steps]); // Create a copy of steps
  };

  const handleStepUpdate = (stepId: string, status: Step['status'], details?: string) => {
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === stepId 
          ? { ...step, status, details, timestamp: Date.now() }
          : step
      )
    );
  };

  const handleExecute = async () => {
    if (!selectedFeature) return;
    
    setIsExecuting(true);
    
    // Reset all steps to pending
    setSteps(prevSteps => 
      prevSteps.map(step => ({ ...step, status: 'pending', details: undefined, timestamp: undefined }))
    );
    
    // Execute steps
    await executeSteps(steps, handleStepUpdate, executionSpeed);
    
    setIsExecuting(false);
  };

  const totalSteps = getTotalSteps();
  const completedFeatures = Object.values(allFeatures).filter(f => 
    steps.length > 0 && f.steps[0].id === steps[0]?.id && 
    steps.every(s => s.status === 'success')
  ).length;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-xl p-8">
        <h1 className="text-3xl font-bold mb-4">🎯 GoalYet - Project Step Tracker</h1>
        <p className="text-xl opacity-90 mb-6">
          Every feature broken down into atomic steps. Track, visualize, and understand your entire system.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">{totalSteps}</div>
            <div className="text-sm opacity-90">Total Steps Mapped</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">{Object.keys(allFeatures).length}</div>
            <div className="text-sm opacity-90">Features Tracked</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">
              {((totalSteps / 38700000000000) * 100).toExponential(2)}%
            </div>
            <div className="text-sm opacity-90">of M4 Chip/sec</div>
          </div>
        </div>
      </div>

      {/* Feature Selection */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-bold mb-4">Select a Feature to Track</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(allFeatures).map(([key, feature]) => (
            <button
              key={key}
              onClick={() => handleFeatureSelect(key as keyof typeof allFeatures)}
              className={`p-4 rounded-lg border-2 transition-all hover:shadow-lg ${
                selectedFeature === key
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold mb-1">{feature.name}</div>
              <div className="text-sm text-gray-600">{feature.steps.length} steps</div>
              <div className="text-xs text-gray-500 mt-1">⏱ {feature.estimatedTime}</div>
              <div className={`text-xs mt-1 px-2 py-1 rounded-full inline-block ${
                feature.complexity === 'High' ? 'bg-red-100 text-red-700' :
                feature.complexity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {feature.complexity}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Execution Controls */}
      {selectedFeature && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Execution Controls</h3>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <span className="text-sm">Speed:</span>
                <select
                  value={executionSpeed}
                  onChange={(e) => setExecutionSpeed(Number(e.target.value))}
                  className="px-3 py-1 border rounded-lg text-sm"
                  disabled={isExecuting}
                >
                  <option value={100}>Very Fast (0.1s)</option>
                  <option value={250}>Fast (0.25s)</option>
                  <option value={500}>Normal (0.5s)</option>
                  <option value={1000}>Slow (1s)</option>
                  <option value={2000}>Very Slow (2s)</option>
                </select>
              </label>
              <button
                onClick={handleExecute}
                disabled={isExecuting}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  isExecuting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {isExecuting ? '⚡ Executing...' : '▶️ Execute Steps'}
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Simulate the execution of {allFeatures[selectedFeature].name} with {allFeatures[selectedFeature].steps.length} steps.
          </div>
        </div>
      )}

      {/* Step Tracker */}
      {selectedFeature && steps.length > 0 && (
        <StepTracker
          title={allFeatures[selectedFeature].name}
          steps={steps}
          onStepUpdate={handleStepUpdate}
          showTimeline={true}
          showCategories={true}
        />
      )}

      {/* Insights */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4">💡 Why Step Tracking Matters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-semibold mb-2">🔍 Complete Visibility</h4>
            <p className="text-sm text-gray-600">
              Every action, API call, database query, and state change is tracked. Nothing is hidden.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-semibold mb-2">🐛 Debug Faster</h4>
            <p className="text-sm text-gray-600">
              When something fails, you know exactly which step and can trace the issue immediately.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-semibold mb-2">📊 Performance Insights</h4>
            <p className="text-sm text-gray-600">
              Identify bottlenecks by seeing which steps take longest and optimize accordingly.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-semibold mb-2">📚 Documentation</h4>
            <p className="text-sm text-gray-600">
              Steps become living documentation of how your system actually works.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};